import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@utils/index';
import React from 'react';

import { WORKFORCE_COLUMNS } from '../../data/columns';

const SKELETON_ROW_COUNT = 3;
const CARDS_PER_CELL = 2;

/**
 * Skeleton mirroring the dimensions of `WorkforceGrid` so the layout doesn't
 * jump when real data arrives. Reuses `WORKFORCE_COLUMNS` for the column count
 * so additions there are reflected here automatically.
 */
export function WorkforceGridSkeleton() {
  const gridStyle = {
    gridTemplateColumns: `220px repeat(${WORKFORCE_COLUMNS.length}, minmax(190px, 1fr))`,
  } as const;

  const headerCellBase = 'sticky top-0 z-10 bg-muted border-b border-r border-border px-3 py-2.5';
  const stickyAgentBase = 'sticky left-0 z-10 bg-card border-r border-border';

  return (
    <div className="h-full overflow-auto" aria-hidden="true">
      <div className="grid min-w-max" style={gridStyle}>
        {/* Top-left corner */}
        <div
          className={cn(
            'sticky top-0 left-0 z-20 bg-muted border-b border-r border-border px-3 py-2.5',
            'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
          )}
        >
          Agent
        </div>
        {/* Column headers — real labels, skeleton count */}
        {WORKFORCE_COLUMNS.map(column => (
          <div key={column.id} className={cn(headerCellBase, 'last:border-r-0')}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
                {column.label}
              </span>
              <Skeleton className="h-4 w-6" />
            </div>
          </div>
        ))}

        {/* Skeleton agent rows */}
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
          <React.Fragment key={rowIdx}>
            <div className={cn(stickyAgentBase, rowIdx > 0 && 'border-t')}>
              <div className="flex items-start gap-2 py-3 pl-3 pr-2 h-full">
                <Skeleton className="size-2 rounded-full mt-1.5 shrink-0" />
                <div className="flex flex-col gap-1.5 min-w-0">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-12" />
                </div>
              </div>
            </div>
            {WORKFORCE_COLUMNS.map(column => (
              <div
                key={column.id}
                className={cn(
                  'border-r border-border last:border-r-0 p-2 space-y-2 align-top bg-background',
                  rowIdx > 0 && 'border-t',
                )}
              >
                {Array.from({ length: CARDS_PER_CELL }).map((__, cardIdx) => (
                  <Skeleton key={cardIdx} className="h-[120px] w-full rounded-md" />
                ))}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
